import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  StepLabel,
  Tab,
  Tabs,
} from "@mui/material";

import { ReactComponent as IconClose } from "assets/svg/20-close.svg";

import { isOpenDialogSelector } from "redux-store/static/redux/selector";
import { clearDialog, setupDialog } from "redux-store/static/redux/dialog";

import { DIALOG } from "helper/enum/dialogEnum";

import { Title, DialogTitleCustom, StepWrapper, TextBlue } from "./style";
import pxToRem from "theme/base/pxToRem";

import { resetQrStatusSelector } from "modules/Auth/redux/selector";
import { verifyIncomeOtpStatusSelector } from "modules/Salary/redux/selector";
import {
  clearVerifyIncomeOtpStatus,
  verifyIncomeOtpStart,
} from "modules/Salary/redux/verifyIncomeOtp";
import type { IncomeOtpMode } from "modules/Salary/types/incomeOtpMode";
import { cancelIncomeSectionOtp } from "core/http/incomeOtpBridge";

import { ReduxStateType } from "redux-store/types";
import TabHasApp from "./TabHasApp";
import TabNoApp from "./TabNoApp";
import { ArrowRight } from "@mui/icons-material";

const VERIFY_DIALOG_KEY = DIALOG.MODAL_VERIFY_INCOME;

const ModalVerify = () => {
  const dispatch = useDispatch();

  const open = useSelector(isOpenDialogSelector(VERIFY_DIALOG_KEY));
  const resetQrLoading =
    useSelector(resetQrStatusSelector) === ReduxStateType.LOADING;
  const verifyOtpStatus = useSelector(verifyIncomeOtpStatusSelector);
  const verifyOtpLoading = verifyOtpStatus === ReduxStateType.LOADING;

  const busy = verifyOtpLoading || resetQrLoading;
  const [tabValue, setTabValue] = useState("1");
  const [formResetKey, setFormResetKey] = useState(0);

  useEffect(() => {
    dispatch(setupDialog({ key: VERIFY_DIALOG_KEY }));
  }, [dispatch]);

  const handleSubmitOtp = useCallback(
    (otp: string, mode: IncomeOtpMode) => {
      if (otp.length !== 6 || busy) return;
      dispatch(verifyIncomeOtpStart({ otp, mode }));
    },
    [busy, dispatch]
  );

  useEffect(() => {
    if (verifyOtpStatus !== ReduxStateType.SUCCESS || !open) return;
    dispatch(clearDialog({ key: VERIFY_DIALOG_KEY }));
    setFormResetKey((k) => k + 1);
    dispatch(clearVerifyIncomeOtpStatus());
  }, [verifyOtpStatus, open, dispatch]);

  const handleClose = useCallback(() => {
    if (busy) return;
    cancelIncomeSectionOtp();
    dispatch(clearDialog({ key: VERIFY_DIALOG_KEY }));
    setFormResetKey((k) => k + 1);
  }, [busy, dispatch]);

  const onSubmitExistingOtp = useCallback(
    (otp: string) => handleSubmitOtp(otp, "existing"),
    [handleSubmitOtp]
  );

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: string) => {
      setTabValue(newValue);
    },
    []
  );

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (
          busy &&
          (reason === "backdropClick" || reason === "escapeKeyDown")
        ) {
          return;
        }
        handleClose();
      }}
      maxWidth="lg"
    >
      <DialogTitleCustom>
        <Title>Nhập mã xác thực</Title>
        <IconButton onClick={handleClose} disabled={busy}>
          <IconClose />
        </IconButton>
      </DialogTitleCustom>
      <DialogContent dividers>
        <StepWrapper>
          <ArrowRight color="primary" />
          <StepLabel>
            Nếu đã cài đặt <TextBlue>"Google Authenticator"</TextBlue> trên điện
            thoại di động,
            <br /> chọn tab <TextBlue>"Nhập mã"</TextBlue>
          </StepLabel>
        </StepWrapper>
        <StepWrapper>
          <ArrowRight color="primary" />
          <StepLabel>
            Nếu chưa cài đặt, chọn tab <TextBlue>"Cài đặt ứng dụng"</TextBlue>{" "}
            và làm theo hướng dẫn
          </StepLabel>
        </StepWrapper>
        <Stack gap={pxToRem(18)}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="Xác thực tra cứu thu nhập"
              variant="fullWidth"
            >
              <Tab label="Nhập mã" value="1" />
              <Tab label="Cài đặt ứng dụng" value="2" />
            </Tabs>
          </Box>
          {tabValue === "1" && (
            <TabHasApp
              key={formResetKey}
              submitting={verifyOtpLoading}
              onSubmitOtp={onSubmitExistingOtp}
            />
          )}
          {tabValue === "2" && (
            <TabNoApp
              key={formResetKey}
              onSubmitOtp={onSubmitExistingOtp}
              submitting={verifyOtpLoading}
              resetBusy={resetQrLoading}
            />
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default ModalVerify;
